import React, { useEffect, useMemo, useState } from 'react';

import {

    Dialog,

    DialogTitle,

    DialogContent,

    DialogActions,

    Button,

    List,

    ListItem,

    ListItemText,

    ListItemIcon,

    Typography,

    useTheme,

    alpha,

    Checkbox,

    Box,

    Stack,

    CircularProgress,

} from '@mui/material';

import {

    Description as FileIcon,

    AttachFile as AttachmentIcon,

    Archive as ArchiveIcon,

    Download as DownloadIcon,

    Visibility as ViewIcon,

} from '@mui/icons-material';

import { isOfficeDocumentFilename } from '../utils/filePreview';
import { CrsCategoryIcon, FileCategoryChip } from './FileCategoryChip';



// Nautilus Console Design System Colors

const NAUTILUS = {

    deepOcean: '#0f2134',

    sand: '#ccba9b',

    abyssal: '#0a1622',

};



export interface DownloadableFileItem {

    id: number;

    filename: string;

    category: string;

    size: number;

    mime_type?: string;

    has_path?: boolean;

    processing_status?: string;

    /** version-bound files vs revision-level auxiliary files */

    source?: 'version' | 'aux';

    description?: string;

    /** non-numeric identifier for project templates */

    templateId?: string;

}



export interface DownloadSelectionPanelProps {

    files: DownloadableFileItem[];

    onView: (file: DownloadableFileItem) => void;

    onDownload: (file: DownloadableFileItem) => void;

    onDownloadAll: (selectedFiles: DownloadableFileItem[]) => Promise<void> | void;

    isDownloadingAll?: boolean;

    compact?: boolean;

    showHint?: boolean;

    title?: string;

}



interface DownloadSelectionDialogProps extends DownloadSelectionPanelProps {

    open: boolean;

    onClose: () => void;

    loading?: boolean;

}



export const getDownloadableFileKey = (file: DownloadableFileItem) =>

    file.templateId ? `template-${file.templateId}` : `${file.source || 'version'}-${file.id}`;



export const isDownloadableFileReady = (file: DownloadableFileItem) =>

    (file.processing_status || 'ready') === 'ready' && file.has_path !== false;



export const isNativeDownloadableFile = (file: DownloadableFileItem) =>

    (file.category || '').toLowerCase() === 'native';



export const getDefaultZipSelectionKeys = (files: DownloadableFileItem[]) =>

    new Set(

        files

            .filter((file) => isDownloadableFileReady(file) && !isNativeDownloadableFile(file))

            .map(getDownloadableFileKey)

    );



const truncateFilename = (filename: string, maxBaseLength = 55) => {

    const dotIndex = filename.lastIndexOf('.');

    const hasExtension = dotIndex > 0 && dotIndex < filename.length - 1;

    const extension = hasExtension ? filename.slice(dotIndex) : '';

    const base = hasExtension ? filename.slice(0, dotIndex) : filename;

    if (base.length <= maxBaseLength) return `${base}${extension}`;

    return `${base.slice(0, maxBaseLength - 3)}...${extension}`;

};



const getCategoryIcon = (category: string, isDark: boolean, compact: boolean) => {

    const iconColor = isDark ? NAUTILUS.sand : NAUTILUS.deepOcean;

    const iconSx = { color: iconColor, fontSize: compact ? '1rem' : undefined };

    switch ((category || '').toLowerCase()) {

        case 'main':

            return <FileIcon sx={iconSx} />;

        case 'attachment':

            return <AttachmentIcon sx={iconSx} />;

        case 'archive':

            return <ArchiveIcon sx={iconSx} />;

        case 'crs':

            return <CrsCategoryIcon compact={compact} />;

        default:

            return <FileIcon sx={iconSx} />;

    }

};



export const DownloadSelectionPanel: React.FC<DownloadSelectionPanelProps> = ({

    files,

    onView,

    onDownload,

    onDownloadAll,

    isDownloadingAll = false,

    compact = false,

    showHint = true,

    title,

}) => {

    const theme = useTheme();

    const isDark = theme.palette.mode === 'dark';

    const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';



    const [zipSelectedKeys, setZipSelectedKeys] = useState<Set<string>>(() =>

        getDefaultZipSelectionKeys(files)

    );



    useEffect(() => {

        setZipSelectedKeys(getDefaultZipSelectionKeys(files));

    }, [files]);



    const zipSelectedFiles = useMemo(

        () =>

            files.filter(

                (file) =>

                    zipSelectedKeys.has(getDownloadableFileKey(file)) && isDownloadableFileReady(file)

            ),

        [files, zipSelectedKeys]

    );



    const toggleZipSelection = (file: DownloadableFileItem, checked: boolean) => {

        const key = getDownloadableFileKey(file);

        setZipSelectedKeys((prev) => {

            const next = new Set(prev);

            if (checked) {

                next.add(key);

            } else {

                next.delete(key);

            }

            return next;

        });

    };



    const actionButtonSx = {

        color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,

        fontWeight: 600,

        fontSize: compact ? '0.6rem' : '0.7rem',

        letterSpacing: '0.05em',

        textTransform: 'uppercase' as const,

        minWidth: compact ? 0 : undefined,

        px: compact ? 0.5 : 1,

        py: compact ? 0.15 : 0.25,

        '&:hover': {

            bgcolor: alpha(NAUTILUS.sand, 0.15),

        },

        '& .MuiButton-startIcon': {

            marginRight: compact ? 0.2 : 0.35,

            '& > *:nth-of-type(1)': { fontSize: compact ? '0.75rem' : '0.85rem' },

        },

    };



    const filenameMaxLength = compact ? 28 : 55;



    return (

        <Box

            sx={{

                border: compact ? `1px solid ${alpha(panelBorder, 0.7)}` : undefined,

                borderRadius: compact ? 1 : undefined,

                p: compact ? 1 : 0,

                bgcolor: compact

                    ? isDark

                        ? alpha(NAUTILUS.abyssal, 0.35)

                        : alpha('#f5f5f5', 0.5)

                    : undefined,

            }}

        >

            {title && (

                <Typography

                    variant="caption"

                    sx={{

                        display: 'block',

                        mb: compact ? 0.75 : 1,

                        fontWeight: 600,

                        letterSpacing: '0.06em',

                        textTransform: 'uppercase',

                        color: isDark ? '#b0bec5' : '#546e7a',

                        fontSize: compact ? '0.65rem' : '0.75rem',

                    }}

                >

                    {title}

                </Typography>

            )}

            {showHint && (

                <Typography

                    variant="body2"

                    sx={{

                        color: isDark ? '#b0bec5' : '#546e7a',

                        mb: compact ? 1 : 2,

                        fontSize: compact ? '0.6rem' : '0.75rem',

                        letterSpacing: '0.05em',

                        textTransform: 'uppercase',

                        lineHeight: 1.4,

                    }}

                >

                    Check files for ZIP. View opens a preview; Download saves the file.

                </Typography>

            )}

            <List

                sx={{

                    p: 0,

                    maxHeight: compact ? 180 : undefined,

                    overflowY: compact ? 'auto' : undefined,

                }}

            >

                {files.map((file) => {

                    const fileKey = getDownloadableFileKey(file);

                    const isReady = isDownloadableFileReady(file);

                    const canView = !isOfficeDocumentFilename(file.filename);

                    const isZipSelected = zipSelectedKeys.has(fileKey);

                    return (

                        <ListItem

                            key={fileKey}

                            sx={{

                                borderRadius: 1,

                                mb: compact ? 0.35 : 0.5,

                                py: compact ? 0.35 : undefined,

                                pr: compact ? '138px !important' : '128px !important',

                                border: `1px solid ${alpha(panelBorder, 0.5)}`,

                                bgcolor: isDark

                                    ? alpha(NAUTILUS.abyssal, 0.5)

                                    : alpha('#f5f5f5', 0.5),

                                opacity: isReady ? 1 : 0.65,

                                transition: 'all 0.15s ease',

                                '&:hover': {

                                    bgcolor: isDark

                                        ? alpha(NAUTILUS.sand, 0.1)

                                        : alpha(NAUTILUS.deepOcean, 0.05),

                                    borderColor: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,

                                },

                            }}

                            secondaryAction={

                                <Stack direction="row" spacing={0.25} alignItems="center">

                                    {canView && (

                                    <Button

                                        size="small"

                                        startIcon={<ViewIcon />}

                                        onClick={(event) => {

                                            event.stopPropagation();

                                            if (isReady) onView(file);

                                        }}

                                        sx={actionButtonSx}

                                        disabled={!isReady}

                                    >

                                        View

                                    </Button>

                                    )}

                                    <Button

                                        size="small"

                                        startIcon={<DownloadIcon />}

                                        onClick={(event) => {

                                            event.stopPropagation();

                                            if (isReady) onDownload(file);

                                        }}

                                        sx={actionButtonSx}

                                        disabled={!isReady}

                                    >

                                        {isReady ? 'Download' : '...'}

                                    </Button>

                                </Stack>

                            }

                        >

                            <ListItemIcon sx={{ minWidth: compact ? 28 : 36 }}>

                                <Checkbox

                                    edge="start"

                                    size={compact ? 'small' : 'medium'}

                                    checked={isZipSelected}

                                    disabled={!isReady || isDownloadingAll}

                                    onChange={(event) => {

                                        event.stopPropagation();

                                        toggleZipSelection(file, event.target.checked);

                                    }}

                                    onClick={(event) => event.stopPropagation()}

                                    sx={{

                                        p: compact ? 0.25 : undefined,

                                        color: isDark ? '#b0bec5' : '#546e7a',

                                        '&.Mui-checked': {

                                            color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,

                                        },

                                    }}

                                />

                            </ListItemIcon>

                            <ListItemIcon sx={{ minWidth: compact ? 28 : 40 }}>

                                {getCategoryIcon(file.category, isDark, compact)}

                            </ListItemIcon>

                            <ListItemText

                                primary={

                                    <Typography

                                        title={file.filename}

                                        noWrap

                                        sx={{

                                            fontWeight: 500,

                                            fontSize: compact ? '0.75rem' : undefined,

                                            color: isDark ? '#ffffff' : NAUTILUS.deepOcean,

                                            overflow: 'hidden',

                                            textOverflow: 'ellipsis',

                                        }}

                                    >

                                        {truncateFilename(file.filename, filenameMaxLength)}

                                    </Typography>

                                }

                                secondary={

                                    <Box

                                        component="span"

                                        sx={{

                                            display: 'flex',

                                            alignItems: 'center',

                                            gap: 0.75,

                                            flexWrap: 'wrap',

                                        }}

                                    >

                                        <Typography

                                            component="span"

                                            noWrap

                                            sx={{

                                                fontSize: compact ? '0.6rem' : '0.75rem',

                                                fontFamily: 'monospace',

                                                letterSpacing: '0.03em',

                                                color: isDark ? '#b0bec5' : '#546e7a',

                                            }}

                                        >

                                            {(file.size / 1024 / 1024).toFixed(2)} MB

                                            {!isReady ? ` — ${(file.processing_status || 'processing').toUpperCase()}` : ''}

                                            {!compact && file.description ? ` — ${file.description}` : ''}

                                        </Typography>

                                        <FileCategoryChip

                                            category={file.category}

                                            labelStyle="uppercase"

                                            prefix={file.source === 'aux' ? 'AUX' : undefined}

                                        />

                                    </Box>

                                }

                            />

                        </ListItem>

                    );

                })}

            </List>

            <Box

                sx={{

                    display: 'flex',

                    alignItems: 'center',

                    gap: compact ? 0.75 : 1,

                    mt: compact ? 0.75 : 1.5,

                    flexWrap: 'wrap',

                }}

            >

                <Typography

                    variant="caption"

                    sx={{

                        flex: 1,

                        color: isDark ? '#b0bec5' : '#546e7a',

                        letterSpacing: '0.04em',

                        fontSize: compact ? '0.6rem' : undefined,

                    }}

                >

                    {zipSelectedFiles.length} selected for ZIP

                </Typography>

                <Button

                    size={compact ? 'small' : 'medium'}

                    onClick={(event) => {

                        event.stopPropagation();

                        void onDownloadAll(zipSelectedFiles);

                    }}

                    disabled={zipSelectedFiles.length === 0 || isDownloadingAll}

                    startIcon={<ArchiveIcon sx={{ fontSize: compact ? '0.9rem' : undefined }} />}

                    sx={{

                        color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,

                        fontWeight: 600,

                        fontSize: compact ? '0.65rem' : undefined,

                        letterSpacing: '0.03em',

                        py: compact ? 0.25 : undefined,

                        px: compact ? 1 : undefined,

                    }}

                >

                    {isDownloadingAll ? 'ZIP...' : 'Download ZIP'}

                </Button>

            </Box>

        </Box>

    );

};



const DownloadSelectionDialog: React.FC<DownloadSelectionDialogProps> = ({

    open,

    onClose,

    files,

    onView,

    onDownload,

    onDownloadAll,

    isDownloadingAll = false,

    loading = false,

}) => {

    const theme = useTheme();

    const isDark = theme.palette.mode === 'dark';

    const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';

    const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';



    return (

        <Dialog

            open={open}

            onClose={onClose}

            maxWidth="md"

            fullWidth

            PaperProps={{

                sx: {

                    bgcolor: surfaceBg,

                    border: `1px solid ${panelBorder}`,

                    borderRadius: 1,

                },

            }}

        >

            <DialogTitle

                sx={{

                    borderBottom: `1px solid ${panelBorder}`,

                    fontWeight: 600,

                    letterSpacing: '-0.01em',

                    color: isDark ? '#ffffff' : NAUTILUS.deepOcean,

                }}

            >

                Download Files

            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>

                {loading ? (

                    <Box display="flex" alignItems="center" justifyContent="center" gap={1.5} py={5}>

                        <CircularProgress size={22} />

                        <Typography variant="body2" color="text.secondary">

                            Loading files...

                        </Typography>

                    </Box>

                ) : files.length === 0 ? (

                    <Box py={5} textAlign="center">

                        <Typography variant="body2" color="text.secondary">

                            No files were uploaded with this version.

                        </Typography>

                    </Box>

                ) : (

                    <DownloadSelectionPanel

                        files={files}

                        onView={onView}

                        onDownload={onDownload}

                        onDownloadAll={onDownloadAll}

                        isDownloadingAll={isDownloadingAll}

                    />

                )}

            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}` }}>

                <Button

                    onClick={onClose}

                    sx={{

                        color: 'text.secondary',

                        fontWeight: 500,

                    }}

                >

                    Close

                </Button>

            </DialogActions>

        </Dialog>

    );

};



export default DownloadSelectionDialog;

